import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/index.js';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    orgId: '',
    password: '',
    username: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [connectionStatus, setConnectionStatus] = useState('checking');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Check connection status on component mount
  useEffect(() => {
    checkConnection();
  }, []);

  const checkConnection = async () => {
    try {
      setConnectionStatus('checking');
      const isConnected = await authService.testConnection();
      setConnectionStatus(isConnected ? 'connected' : 'disconnected');
    } catch (error) {
      console.error('Connection check failed:', error);
      setConnectionStatus('disconnected');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError(''); // Clear error when user types
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // First check if we can connect to the API
      if (connectionStatus === 'disconnected') {
        throw new Error('Cannot connect to server. Please check if the backend is running.');
      }

      // Try to authenticate with the real API
      try {
        const response = await authService.login(formData.orgId, formData.password, formData.username);
        
        if (response && response.token) {
          if (response.role === 'DRIVER') {
            throw new Error('Drivers are not permitted to log in to the Web Admin panel. Please use the mobile app.');
          }
          // Real API authentication successful
          setSuccess(true);
          setTimeout(() => {
            try {
              if (authService.setTokens) {
                authService.setTokens(response.token, response.refreshToken);
              }
              localStorage.setItem('authToken', response.token);
              if (response.role) localStorage.setItem('userRole', response.role);
              if (response.userType) localStorage.setItem('userType', response.userType);

              // Pass organization data along with token
              const orgData = {
                orgId: response.orgId,
                orgName: response.orgName,
                email: response.email,
                phoneNumber: response.phoneNumber,
                active: response.active,
                multiLevel: response.multiLevel,
                institutionType: response.institutionType,
                address: response.address,
                userType: response.userType,
                userId: response.userId,
                role: response.role,
                principalName: response.principalName,
                affiliation: response.affiliation,
                image: response.image,
                logo: response.image,
                logoUrl: response.image,
                name: response.name || response.username,
                username: response.username
              };

              localStorage.setItem('orgData', JSON.stringify(orgData));
              if (response.orgId) localStorage.setItem('orgId', String(response.orgId));
              if (response.name || response.username) {
                localStorage.setItem('username', response.name || response.username);
                localStorage.setItem('name', response.name || response.username);
              }
              if (response.image) {
                localStorage.setItem('schoolLogo', response.image);
                localStorage.setItem('orgLogo', response.image);
                localStorage.setItem('logoUrl', response.image);
              }

              if (typeof onLogin === 'function') {
                onLogin(response.token, orgData);
              }

              // Navigate to admin dashboard
              navigate('/admin', { replace: true });

              // Fallback redirect if route didn't change
              setTimeout(() => {
                if (window.location.pathname === '/login' || window.location.pathname === '/') {
                  window.location.href = '/admin';
                }
              }, 200);
            } catch (err) {
              console.error('Error during post-login redirect:', err);
              window.location.href = '/admin';
            }
          }, 400);
        } else if (response && response.success) {
          // Handle different response format
          const token = response.token || 'api-token-' + Date.now();
          setSuccess(true);
          setTimeout(() => {
            try {
              if (authService.setTokens) {
                authService.setTokens(token);
              }
              localStorage.setItem('authToken', token);
              if (typeof onLogin === 'function') {
                onLogin(token);
              }
              navigate('/admin', { replace: true });
              setTimeout(() => {
                if (window.location.pathname === '/login' || window.location.pathname === '/') {
                  window.location.href = '/admin';
                }
              }, 200);
            } catch (err) {
              console.error('Error during fallback login redirect:', err);
              window.location.href = '/admin';
            }
          }, 400);
        } else {
          throw new Error('Invalid response from server');
        }
      } catch (apiError) {
        console.error('API login failed:', apiError);
        throw new Error('Login failed. Please check your credentials and try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Floating Background Shapes */}
      <div className="floating-shapes">
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
        <div className="floating-shape"></div>
      </div>
      
      <div className="login-card">
        {/* Left Branding Section */}
        <div className="login-branding">
          <div className="branding-logo">
            🎓
          </div>
          
          <p className="branding-subtitle">Smart College & School Management System</p>
          <ul className="branding-features">
            <li>Secure Student Management</li>
            <li>Advanced Analytics Dashboard</li>
            <li>Real-time Data Synchronization</li>
          </ul>
        </div>
        
        {/* Right Form Section */}
        <div className="login-form-section">
          <h2 className="login-title">Welcome Back</h2>
        
          {/* Professional Connection Status */}
          <div className="connection-status" style={{ 
            marginBottom: '15px', 
            textAlign: 'center',
            background: connectionStatus === 'connected' ? '#f0f9ff' : 
                       connectionStatus === 'disconnected' ? '#fef2f2' : '#f8fafc',
            border: `1px solid ${connectionStatus === 'connected' ? '#bfdbfe' : 
                                 connectionStatus === 'disconnected' ? '#fecaca' : '#e2e8f0'}`,
            borderRadius: '8px',
            padding: '10px 14px'
          }}>
            <div style={{ 
              color: connectionStatus === 'connected' ? '#1e40af' : 
                     connectionStatus === 'disconnected' ? '#dc2626' : '#64748b',
              fontWeight: '600',
              fontSize: '13px',
              marginBottom: '4px'
            }}>
              🌐 Server Status
            </div>
            <div style={{
              color: connectionStatus === 'connected' ? '#059669' : 
                     connectionStatus === 'disconnected' ? '#dc2626' : '#64748b',
              fontWeight: '500',
              fontSize: '12px'
            }}>
              {
                connectionStatus === 'connected' ? '✓ Connected & Ready' :
                connectionStatus === 'disconnected' ? '⚠ Offline Mode (Mock Data)' :
                '🔄 Checking Connection...'
              }
            </div>
            {connectionStatus === 'disconnected' && (
              <button 
                onClick={checkConnection}
                style={{ 
                  marginTop: '8px', 
                  padding: '4px 12px', 
                  fontSize: '12px',
                  border: '1px solid #007bff',
                  backgroundColor: 'transparent',
                  color: '#007bff',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Retry
              </button>
            )}
          </div>
        
        {error && (
          <div className="alert alert-danger">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="orgId">Organization ID</label>
            <input
              type="text"
              id="orgId"
              name="orgId"
              className="form-control"
              value={formData.orgId}
              onChange={handleChange}
              disabled={loading}
              placeholder="Required for admin, optional for staff"
            />
          </div>
          <div className="form-group">
            <label htmlFor="username">Username / Email (Staff Only)</label>
            <input
              type="text"
              id="username"
              name="username"
              className="form-control"
              value={formData.username}
              onChange={handleChange}
              disabled={loading}
              placeholder="Leave blank for main admin account"
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className="form-control"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                disabled={loading}
                placeholder="Enter password"
                style={{ paddingRight: '44px', width: '100%' }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => !prev)}
                title={showPassword ? "Hide password" : "Show password"}
                aria-label={showPassword ? "Hide password" : "Show password"}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '6px 8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#64748b',
                  borderRadius: '6px',
                  outline: 'none',
                  transition: 'color 0.15s ease, background-color 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#0f172a';
                  e.currentTarget.style.backgroundColor = '#f1f5f9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#64748b';
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className={`login-btn ${loading ? 'loading' : ''} ${success ? 'login-success' : ''}`}
            disabled={loading || success}
            style={{
              background: success ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)' : undefined
            }}
          >
            {success ? (
              <>
                <span style={{ marginRight: '8px' }}>✓</span>
                Login Successful!
              </>
            ) : loading ? (
              <>
                <span style={{ marginRight: '8px' }}>🔄</span>
                Logging in...
              </>
            ) : (
              <>
                <span style={{ marginRight: '8px' }}>🚀</span>
                Login
              </>
            )}
          </button>
          <div style={{ marginTop: '8px', textAlign: 'right' }}>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              style={{
                fontSize: '12px',
                color: '#2a5298',
                textDecoration: 'underline',
                cursor: 'pointer',
                background: 'transparent',
                border: 'none',
                padding: 0
              }}
            >
              Forgot Password?
            </button>
          </div>
        </form>

        </div>
      </div>

      <div className="login-footer">
        © S Square G Tech Solutions Pvt Ltd.
      </div>
    </div>
  );
};

export default Login;
